import { WidgetOptionsProvider } from "arkh";
import React, { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { graphql, useSubscription } from "react-relay";
import { WidgetEmployeeRoleOverview } from "widgets";
import { WidgetEmployeeRoleOverviewContentRef } from "widgets/src/WidgetEmployeeRoleOverview/WidgetEmployeeRoleOverviewContent";
import { WidgetEmployeeRoleOverview_widget$key } from "widgets/src/WidgetEmployeeRoleOverview/__generated__/WidgetEmployeeRoleOverview_widget.graphql";
import { WidgetHeaderOptions } from "widgets/src/WidgetHeaderOptions";
import { WidgetEvent } from "widgets/src/WidgetEventT";
import { EmployeeRoleFocusScrollSubscription } from "./__generated__/EmployeeRoleFocusScrollSubscription.graphql";
import { TVEmployeeRoleDialog } from "./TVEmployeeRoleDialog";

export function EmployeeRoleFocus(props: {
  widgetId: string;
  widget: WidgetEmployeeRoleOverview_widget$key;
  teams: readonly {
    readonly name: string;
    readonly icon: any;
    readonly color: string | null;
  }[];
  detailId?: string;
  touchMode?: boolean;
  storecastId?: string;
}) {
  const ref = useRef<WidgetEmployeeRoleOverviewContentRef>(null);
  const touchModeRef = useRef(props.touchMode);
  touchModeRef.current = props.touchMode;

  useSubscription<EmployeeRoleFocusScrollSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription EmployeeRoleFocusScrollSubscription($widgetId: ID!) {
            widgetFocusScrollChanged(widgetId: $widgetId)
          }
        `,
        variables: { widgetId: props.widgetId },
        onNext: (data) => {
          if (touchModeRef.current) return;
          if (data?.widgetFocusScrollChanged)
            ref.current?.scrollTo(data.widgetFocusScrollChanged);
        },
      }),
      [props.widgetId]
    )
  );

  const headerRight = useMemo(
    () => <WidgetHeaderOptions teams={props.teams} />,
    [props.teams]
  );

  const widgetOptions = useMemo(
    () => ({ headerRight, background: undefined }),
    [headerRight]
  );

  const [roleDialogData, setRoleDialogData] = useState<{
    roleId: string;
    roleName: string;
    teamId: string;
    teamName: string;
    teamColor: string | null;
    employeeIds: string[];
  } | null>(null);

  const onEvent = useCallback((event: WidgetEvent) => {
    if (event.kind === "EmployeeRoleSelect") {
      setRoleDialogData({
        roleId: event.roleId,
        roleName: event.roleName,
        teamId: event.teamId,
        teamName: event.teamName,
        teamColor: event.teamColor,
        employeeIds: event.employeeIds,
      });
    }
  }, []);

  return (
    <WidgetOptionsProvider value={widgetOptions}>
      <WidgetEmployeeRoleOverview
        ref={ref}
        widget={props.widget}
        mode="IDLE"
        storecastId={props.storecastId}
        idiom={props.touchMode ? "TOUCH" : undefined}
        isFocused
        onEvent={props.touchMode ? onEvent : undefined}
        scrollToItemId={props.detailId}
      />
      {roleDialogData ? (
        <Suspense fallback={null}>
          <TVEmployeeRoleDialog
            roleId={roleDialogData.roleId}
            roleName={roleDialogData.roleName}
            teamId={roleDialogData.teamId}
            teamName={roleDialogData.teamName}
            teamColor={roleDialogData.teamColor}
            employeeIds={roleDialogData.employeeIds}
            onClose={() => setRoleDialogData(null)}
          />
        </Suspense>
      ) : null}
    </WidgetOptionsProvider>
  );
}
